import { type ClipWindow, formatClock } from "../clipTrack";

// ════════════════════════════════════════════════════════════════════════
// ThumbnailStrip — NEW (Coach Review v2 layout).
// ────────────────────────────────────────────────────────────────────────
// The 12-cell strip shown under the clip track in clip mode. It
// samples evenly across a span slightly wider than the clip window so
// the coach can see the lead-in / lead-out, with the in-window cells
// highlighted. Clicking a cell seeks the video to that time.
//
// The strip does NOT decode real video frames — generating per-frame
// JPEGs needs a server-side thumbnail sheet the M-series backend
// doesn't expose for arbitrary offsets. Each cell is a dark tile with
// its timecode, matching the standalone reference. If a frame-sheet
// endpoint lands later, only this component changes.
// ════════════════════════════════════════════════════════════════════════

export type ThumbnailStripProps = {
  /** Total media duration, in seconds. */
  duration: number;
  /** The active clip window, in seconds. */
  window: ClipWindow;
  /** Click a cell → seek the video to that time. */
  onSeek: (seconds: number) => void;
};

const CELLS = 12;

export function ThumbnailStrip({
  duration,
  window,
  onSeek,
}: ThumbnailStripProps) {
  // Sample a span ~2× the window, centered on the window midpoint,
  // clamped to the media — so the coach sees a little context on
  // each side of the clip.
  const center = (window.start + window.end) / 2;
  const half = window.end - window.start;
  const spanStart = Math.max(0, center - half);
  const spanEnd = Math.min(duration, center + half);
  const span = Math.max(0.001, spanEnd - spanStart);

  return (
    <div
      className="grid grid-cols-12 gap-1"
      data-testid="coach-review-thumbnail-strip"
    >
      {Array.from({ length: CELLS }).map((_, i) => {
        const t = spanStart + (i / (CELLS - 1)) * span;
        const inRange = t >= window.start && t <= window.end;
        return (
          <button
            key={i}
            type="button"
            onClick={() => onSeek(t)}
            data-testid="thumbnail-cell"
            data-in-range={inRange}
            className={[
              "relative aspect-video overflow-hidden rounded bg-navy-900 transition-opacity",
              inRange
                ? "border-2 border-accent opacity-100"
                : "border border-rule opacity-55 hover:opacity-80",
            ].join(" ")}
          >
            <span className="absolute bottom-0.5 left-0.5 rounded-sm bg-navy-900/75 px-1 py-px font-mono text-[8px] text-paper">
              {formatClock(t)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
